"use client";

import type { ParcelPreview } from "@/lib/parcelq/types";
import {
  County_Coverage_Get,
  Coverage_Level_Label,
  type Coverage_Level,
} from "@/lib/parcelq/county-capabilities";
import { Parcel_Map_Preview } from "./parcel-map-preview";
import {
  ParcelQ_Btn_Primary_Class,
  ParcelQ_Btn_Secondary_Class,
  ParcelQ_Card_Class,
  ParcelQ_Card_Header_Class,
  ParcelQ_Label_Mono_Class,
} from "./parcelq-styles";

type Confirmation_State_Props = {
  Parcel: ParcelPreview;
  Confirmation_OnBack: () => void;
  Confirmation_OnContinue: () => void;
};

function Coverage_Tone(Level: Coverage_Level): string {
  if (Level === "full") return "text-emerald-300";
  if (Level === "partial") return "text-amber-200";
  return "text-pq-fg-subtle";
}

export function Confirmation_State({
  Parcel,
  Confirmation_OnBack,
  Confirmation_OnContinue,
}: Confirmation_State_Props) {
  const Address_Line = [Parcel.address, Parcel.city, Parcel.state, Parcel.zip_code]
    .filter(Boolean)
    .join(", ");

  const Caps = County_Coverage_Get(Parcel.county);
  const Coverage = Parcel.coverage ?? {
    county: Parcel.county,
    layers: Caps.Layers,
    can_purchase: Caps.Can_Purchase,
    disclosure: Caps.Disclosure,
  };

  const Layer_Rows: { Label: string; Level: Coverage_Level }[] = [
    { Label: "Assessor", Level: Coverage.layers.assessor },
    { Label: "Tax history", Level: Coverage.layers.tax },
    { Label: "Recorder index", Level: Coverage.layers.recorder },
    { Label: "Deep zoning", Level: Coverage.layers.deep_zoning },
  ];

  return (
    <section className="flex flex-1 flex-col px-6 py-10">
      <div
        className={`mx-auto grid w-full max-w-6xl flex-1 gap-0 overflow-hidden rounded-2xl lg:grid-cols-2 ${ParcelQ_Card_Class}`}
      >
        <div className="flex min-h-[320px] flex-col border-b border-pq-border bg-slate-50 lg:min-h-[480px] lg:border-b-0 lg:border-r">
          <div className={ParcelQ_Card_Header_Class}>
            <p className={ParcelQ_Label_Mono_Class}>Map</p>
          </div>
          <div className="relative flex flex-1 items-center justify-center p-6">
            <Parcel_Map_Preview Parcel={Parcel} />
          </div>
        </div>

        <div className="flex min-h-[320px] flex-col bg-pq-panel lg:min-h-[480px]">
          <div className={ParcelQ_Card_Header_Class}>
            <p className={ParcelQ_Label_Mono_Class}>Parcel record</p>
          </div>

          <div className="flex flex-1 flex-col px-8 py-8">
            <dl className="space-y-5">
              <div>
                <dt className={ParcelQ_Label_Mono_Class}>APN</dt>
                <dd className="mt-1 font-mono text-sm text-pq-fg">{Parcel.apn}</dd>
              </div>
              <div>
                <dt className={ParcelQ_Label_Mono_Class}>Address</dt>
                <dd className="mt-1 text-sm text-slate-200">{Address_Line || "—"}</dd>
              </div>
              <div>
                <dt className={ParcelQ_Label_Mono_Class}>County</dt>
                <dd className="mt-1 text-sm text-slate-200">{Parcel.county || "—"}</dd>
              </div>
              <div>
                <dt className={ParcelQ_Label_Mono_Class}>County data coverage</dt>
                <dd className="mt-2 space-y-1.5">
                  {Layer_Rows.map((Row) => (
                    <div key={Row.Label} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-300">{Row.Label}</span>
                      <span className={Coverage_Tone(Row.Level)}>{Coverage_Level_Label(Row.Level)}</span>
                    </div>
                  ))}
                  <p className="pt-2 text-xs leading-relaxed text-pq-fg-subtle">{Coverage.disclosure}</p>
                </dd>
              </div>
              {Parcel.geometry_warning ? (
                <div>
                  <dt className={ParcelQ_Label_Mono_Class}>Geometry</dt>
                  <dd className="mt-1 text-sm text-amber-200">{Parcel.geometry_warning}</dd>
                </div>
              ) : null}
            </dl>

            <div className="mt-auto flex flex-wrap gap-3 pt-10">
              <button type="button" onClick={Confirmation_OnContinue} className={ParcelQ_Btn_Primary_Class}>
                Select report
              </button>
              <button type="button" onClick={Confirmation_OnBack} className={ParcelQ_Btn_Secondary_Class}>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
